import React from "react";

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <style>{`
    /*
     * Finaculator — Privacy Policy
     * Author : Sridhar Chittiprolu
     * Brand  : NAVY #1F3A5F · BLUE #2E6CA4
     */
    :root {
        --navy:        #1F3A5F;
        --blue:        #2E6CA4;
        --blue-tint:   #EAF1F8;
        --ink:         #243140;
        --muted:       #5C6B7A;
        --line:        #DCE3EC;
        --bg:          #F4F7FB;
        --card:        #FFFFFF;
        --radius:      14px;
        --maxw:        820px;
        --font: "Calibri", "Segoe UI", "Helvetica Neue", Arial, system-ui, sans-serif;
    }

    * { box-sizing: border-box; margin: 0; padding: 0; }

    html { scroll-behavior: smooth; }

    body {
        font-family: var(--font);
        color: var(--ink);
        background: var(--bg);
        line-height: 1.7;
        font-size: 17px;
        -webkit-font-smoothing: antialiased;
        text-rendering: optimizeLegibility;
    }

    a { color: var(--blue); text-decoration: none; }
    a:hover, a:focus { text-decoration: underline; }
    a:focus-visible { outline: 3px solid var(--blue); outline-offset: 2px; border-radius: 3px; }

    /* ---------- Header ---------- */
    header.masthead {
        background: linear-gradient(135deg, var(--navy) 0%, #2A4F7C 100%);
        color: #fff;
        padding: 48px 24px 56px;
    }
    .masthead .wrap {
        max-width: var(--maxw);
        margin: 0 auto;
    }
    .brand {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 28px;
    }
    .brand .mark {
        width: 44px;
        height: 44px;
        flex: 0 0 44px;
        background: rgba(255,255,255,0.12);
        border: 1px solid rgba(255,255,255,0.25);
        border-radius: 10px;
        display: grid;
        place-items: center;
    }
    .brand .mark svg { width: 24px; height: 24px; display: block; }
    .brand .name {
        font-size: 21px;
        font-weight: 700;
        letter-spacing: 0.3px;
    }
    .brand .name span { font-weight: 400; opacity: 0.75; }

    .masthead h1 {
        font-size: clamp(28px, 5vw, 40px);
        font-weight: 700;
        line-height: 1.2;
        letter-spacing: -0.5px;
    }
    .masthead .dates {
        margin-top: 16px;
        display: flex;
        flex-wrap: wrap;
        gap: 10px 22px;
        font-size: 14.5px;
        color: rgba(255,255,255,0.82);
    }
    .masthead .dates b { color: #fff; font-weight: 600; }

    /* ---------- Card / body ---------- */
    main {
        max-width: var(--maxw);
        margin: -28px auto 0;
        padding: 0 24px 80px;
    }
    .card {
        background: var(--card);
        border: 1px solid var(--line);
        border-radius: var(--radius);
        padding: 44px clamp(22px, 5vw, 52px);
        box-shadow: 0 12px 40px rgba(31, 58, 95, 0.08);
    }

    .lead {
        font-size: 18px;
        color: var(--ink);
        background: var(--blue-tint);
        border-left: 4px solid var(--blue);
        padding: 18px 22px;
        border-radius: 0 10px 10px 0;
        margin-bottom: 36px;
    }

    section { margin-top: 34px; }
    section:first-of-type { margin-top: 0; }

    h2 {
        font-size: 21px;
        color: var(--navy);
        font-weight: 700;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 2px solid var(--blue-tint);
        scroll-margin-top: 20px;
    }
    h2 .num {
        color: var(--blue);
        font-weight: 700;
        margin-right: 10px;
        font-variant-numeric: tabular-nums;
    }

    p { margin-bottom: 14px; color: var(--ink); }
    p:last-child { margin-bottom: 0; }

    ul { margin: 0 0 14px 4px; padding: 0; list-style: none; }
    ul li {
        position: relative;
        padding-left: 26px;
        margin-bottom: 10px;
        color: var(--ink);
    }
    ul li::before {
        content: "";
        position: absolute;
        left: 4px;
        top: 11px;
        width: 7px;
        height: 7px;
        background: var(--blue);
        border-radius: 50%;
    }

    .pill {
        display: inline-block;
        background: var(--blue-tint);
        color: var(--navy);
        font-size: 13.5px;
        font-weight: 600;
        padding: 3px 12px;
        border-radius: 999px;
        margin-bottom: 14px;
    }

    .contact-box {
        margin-top: 8px;
        background: var(--blue-tint);
        border: 1px solid var(--line);
        border-radius: 10px;
        padding: 20px 22px;
    }
    .contact-box p { margin-bottom: 6px; }
    .contact-box .row { display: flex; gap: 8px; flex-wrap: wrap; }
    .contact-box .row b { color: var(--navy); min-width: 84px; }

    /* ---------- Footer ---------- */
    footer {
        max-width: var(--maxw);
        margin: 0 auto;
        padding: 0 24px 56px;
        text-align: center;
        color: var(--muted);
        font-size: 14px;
    }
    footer .rule {
        height: 1px;
        background: var(--line);
        margin-bottom: 22px;
    }
    footer b { color: var(--navy); }

    /* ---------- Responsive ---------- */
    @media (max-width: 560px) {
        body { font-size: 16px; }
        header.masthead { padding: 36px 18px 48px; }
        main { padding: 0 16px 56px; }
        .card { padding: 30px 20px; }
        footer { padding: 0 16px 44px; }
    }

    @media (prefers-reduced-motion: reduce) {
        html { scroll-behavior: auto; }
    }

    /* ---------- Print ---------- */
    @media print {
        body { background: #fff; font-size: 12pt; }
        header.masthead { background: #fff; color: var(--navy); padding: 0 0 16pt; }
        .brand .mark { border-color: var(--navy); background: none; }
        .masthead .dates { color: var(--muted); }
        .masthead .dates b { color: var(--navy); }
        main { margin: 16pt 0 0; padding: 0; }
        .card { border: none; box-shadow: none; padding: 0; }
        footer { padding: 16pt 0; }
        a { color: var(--ink); }
    }
      `}</style>

      <header className="masthead">
        <div className="wrap">
          <div className="brand">
            <div className="mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.5 3.5a4.5 4.5 0 0 0-4.34 5.7L3 16.36V20.5h4.14l.86-.86v-1.79h1.79l.86-.86v-1.79h1.79l1.66-1.66A4.5 4.5 0 1 0 14.5 3.5Z" stroke="#fff" strokeWidth={1.6} strokeLinejoin="round"/>
                <circle cx="16.5" cy="7.5" r="1.25" fill="#fff"/>
              </svg>
            </div>
            <div className="name">Finaculator <span>· Smart Key</span></div>
          </div>
          <h1>Privacy Policy</h1>
          <div className="dates">
            <span>Effective date: <b>30 June 2026</b></span>
            <span>Last updated: <b>30 June 2026</b></span>
          </div>
        </div>
      </header>

      <main>
        <div className="card">

          <p className="lead">
            Finaculator is built to respect your privacy. The application does not collect, request,
            or store any personal information. It uses only basic, anonymous analytics to understand
            general usage, and it is not operated for any commercial purpose.
          </p>

          <section>
            <h2><span className="num">1</span>Introduction</h2>
            <p>
              This Privacy Policy explains how the Finaculator application and the website
              <a href="https://finaculator.com">finaculator.com</a> (together, "Finaculator", "the app",
              "we", "us", or "our") handle information when you use them. By using Finaculator,
              you agree to the practices described in this policy.
            </p>
            <p>
              Finaculator is a financial calculation tool. It is designed to work without an account,
              without a login, and without collecting anything that personally identifies you.
            </p>
          </section>

          <section>
            <h2><span className="num">2</span>Information we do not collect</h2>
            <p>
              We want to be clear and direct: Finaculator does <b>not</b> collect personal information.
              In particular, we do not ask for, access, or store:
            </p>
            <ul>
              <li>Your name, email address, or phone number</li>
              <li>Account credentials, passwords, or login details</li>
              <li>Bank account numbers, card details, or any financial account information</li>
              <li>Contacts, photos, files, location, or device identifiers tied to you</li>
              <li>Any value you enter into a calculator — these stay on your device and are never sent to us</li>
            </ul>
            <p>
              Any figures you type to perform a calculation are processed on your device only. They are
              not transmitted to our servers and are not retained after you close the app.
            </p>
          </section>

          <section>
            <h2><span className="num">3</span>Analytics</h2>
            <span className="pill">Aggregate &amp; anonymous</span>
            <p>
              Finaculator uses <b>Google Analytics</b> to understand how the app is used in general — for
              example, how many people open it and which screens are visited most. This helps us improve
              the experience.
            </p>
            <p>
              Google Analytics collects only standard, aggregate usage data such as approximate region,
              device type, browser, and pages or screens viewed. This data is anonymous and is not used to
              identify you personally. We do not combine it with any personal information, because we do not
              hold any.
            </p>
            <p>
              Google processes this data under its own terms. You can learn more in
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>
              and review how Google uses data from sites and apps at
              <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">policies.google.com/technologies/partner-sites</a>.
            </p>
          </section>

          <section>
            <h2><span className="num">4</span>Cookies and similar technologies</h2>
            <p>
              On the website, Google Analytics may set basic cookies to count visits and measure general
              traffic. These cookies do not identify you and are used only for aggregate measurement.
              You can block or delete cookies in your browser settings at any time without affecting the
              core functions of Finaculator.
            </p>
          </section>

          <section>
            <h2><span className="num">5</span>No database, no data storage</h2>
            <p>
              Finaculator does not maintain a user database. We do not create user profiles and we do not
              store your inputs, results, or activity on any server we control. There is simply no personal
              data on our side to lose, sell, or share.
            </p>
          </section>

          <section>
            <h2><span className="num">6</span>No commercial use of your data</h2>
            <p>
              We do not sell, rent, trade, or otherwise use any data for advertising, marketing, profiling,
              or other commercial purposes. The limited, anonymous analytics described above are used solely
              to maintain and improve the app.
            </p>
          </section>

          <section>
            <h2><span className="num">7</span>Third-party services</h2>
            <p>
              The only third-party service Finaculator relies on for analytics is Google Analytics, provided
              by Google LLC. Finaculator may also be distributed through app stores such as Google Play,
              which apply their own privacy terms to downloads and store interactions. We do not control and
              are not responsible for the privacy practices of these third parties; please review their
              policies directly.
            </p>
          </section>

          <section>
            <h2><span className="num">8</span>Children's privacy</h2>
            <p>
              Finaculator is intended for a general audience and is not directed at children under the age
              of 13. We do not knowingly collect personal information from children. Since the app does not
              collect personal information from anyone, no such information about children is gathered or held.
            </p>
          </section>

          <section>
            <h2><span className="num">9</span>Data security</h2>
            <p>
              Because Finaculator does not collect or store personal data, the risk to your personal
              information through our app is minimal by design. The anonymous analytics we use are handled
              on Google's secure infrastructure under its own safeguards.
            </p>
          </section>

          <section>
            <h2><span className="num">10</span>Your choices</h2>
            <p>
              You remain in control of the limited analytics data:
            </p>
            <ul>
              <li>Install the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a> to prevent web analytics measurement.</li>
              <li>Adjust your device or browser privacy settings to limit analytics and reset advertising identifiers.</li>
              <li>Clear cookies in your browser to remove existing analytics identifiers.</li>
            </ul>
          </section>

          <section>
            <h2><span className="num">11</span>International users</h2>
            <p>
              Finaculator may be used worldwide. Anonymous analytics data may be processed by Google in
              countries other than your own. By using Finaculator, you understand that this aggregate,
              non-personal data may be processed in those locations under Google's terms.
            </p>
          </section>

          <section>
            <h2><span className="num">12</span>Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in the app or in legal
              requirements. When we do, we will revise the "Last updated" date at the top of this page.
              Continued use of Finaculator after an update means you accept the revised policy.
            </p>
          </section>

          <section>
            <h2><span className="num">13</span>Contact us</h2>
            <p>If you have any questions about this Privacy Policy, you can reach us:</p>
            <div className="contact-box">
              <div className="row"><b>App</b><span>Finaculator</span></div>
              <div className="row"><b>Website</b><span><a href="https://finaculator.com">finaculator.com</a></span></div>
              <div className="row"><b>Email</b><span><a href="mailto:support@finaculator.com">support@finaculator.com</a></span></div>
            </div>
          </section>

        </div>
      </main>

      <footer>
        <div className="rule"></div>
        <p>&copy; 2026 <b>Finaculator</b>. All rights reserved.</p>
        <p>This Privacy Policy is provided to comply with Google Play and general app-store requirements.</p>
      </footer>
    </>
  );
};

export default PrivacyPolicy;